import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, b2bApi } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Stat, StatusPill, Card, fmtDate, fmtDateTime, asList } from "@/components/common/Stat";
import { Input } from "@/components/ui/input";
import { Building2, Users, CreditCard, Layers, MailPlus, Activity, ArrowRight } from "lucide-react";

const useOrgs = () => useQuery({ queryKey: ["owner-orgs"], queryFn: async () => (await api.get("/platform/organizations")).data, retry: false });

export function OwnerDashboard() {
  const t = useT();
  const orgs = useOrgs();
  const stats = useQuery({ queryKey: ["owner-admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, retry: false });
  const billing = useQuery({ queryKey: ["owner-b2b-billing"], queryFn: async () => (await b2bApi.get("/b2b/billing/admin/overview")).data, retry: false });
  const invites = useQuery({ queryKey: ["owner-invites-outbox"], queryFn: async () => (await api.get("/platform/invites/outbox", { params: { limit: 50 } })).data, retry: false });
  const audit = useQuery({ queryKey: ["owner-b2b-audit-recent"], queryFn: async () => (await b2bApi.get("/b2b/admin/audit", { params: { limit: 8 } })).data, retry: false });

  const orgList = asList(orgs.data, "organizations", "items");
  const activeOrgs = orgList.filter((o) => String(o.status || "active").toLowerCase() === "active").length;
  const inv = asList(invites.data, "items", "invites");
  const pending = inv.filter((i) => ["pending", "sent", "queued"].includes(String(i.status || "pending").toLowerCase())).length;
  const s = stats.data || {};
  const totals = billing.data?.totals || {};

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t("b2b.nav.owner")} title={t("b2b.owner.title")} subtitle={t("b2b.owner.subtitle")} />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat icon={Building2} label={t("b2b.owner.orgs")} value={orgList.length} sub={`${activeOrgs} ${t("b2b.owner.activeOrgs").toLowerCase()}`} testId="owner-stat-orgs" />
        <Stat icon={Users} label={t("b2b.owner.users")} value={s.users ?? s.total_users ?? s.user_count} sub={s.active_users != null ? `${s.active_users} active` : undefined} testId="owner-stat-users" />
        <Stat icon={CreditCard} label={t("b2b.owner.activeLicences")} value={totals.active ?? 0} testId="owner-stat-licences" />
        <Stat icon={Layers} label={t("b2b.owner.seats")} value={totals.seats_purchased ?? 0} testId="owner-stat-seats" />
        <Stat icon={MailPlus} label={t("b2b.owner.pendingInvites")} value={pending} testId="owner-stat-invites" />
        <Stat icon={Activity} label={t("nav.usage")} value={s.checkins_7d ?? s.conversations ?? s.messages ?? "—"} testId="owner-stat-usage" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={t("b2b.owner.orgs")} testId="owner-recent-orgs" actions={<Link to="/owner/organizations" className="text-sm text-sage-600 font-semibold inline-flex items-center gap-1">{t("b2b.common.open")} <ArrowRight className="h-3.5 w-3.5" /></Link>}>
          <div className="divide-y divide-border">
            {orgList.slice(0, 6).map((o) => (
              <Link key={o.id || o._id} to={`/owner/organizations/${o.id || o._id}`} className="flex items-center justify-between py-2.5 text-sm hover:bg-cream-50 rounded-lg px-1" data-testid={`owner-org-row-${o.id || o._id}`}>
                <span className="text-ink-900 font-medium truncate">{o.name}</span>
                <StatusPill status={o.status || "active"} />
              </Link>
            ))}
            {!orgList.length && <div className="text-sm text-ink-500 py-3">{t("common.empty")}</div>}
          </div>
        </Card>
        <Card title={t("b2b.owner.recentAudit")} testId="owner-recent-audit">
          <div className="divide-y divide-border">
            {(audit.data?.items || []).map((a) => (
              <div key={a.id} className="py-2.5 text-sm flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-ink-900 font-medium font-mono text-xs">{a.action}</div>
                  <div className="text-xs text-ink-500 truncate">{a.actor_email} · {a.target || "—"}{a.org_id ? ` · ${a.org_id}` : ""}</div>
                </div>
                <div className="text-xs text-ink-500 whitespace-nowrap">{fmtDateTime(a.created_at)}</div>
              </div>
            ))}
            {!(audit.data?.items || []).length && <div className="text-sm text-ink-500 py-3">{t("common.empty")}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function OwnerOrganizations() {
  const t = useT();
  const orgs = useOrgs();
  const billing = useQuery({ queryKey: ["owner-b2b-billing"], queryFn: async () => (await b2bApi.get("/b2b/billing/admin/overview")).data, retry: false });
  const [q, setQ] = useState("");
  const list = asList(orgs.data, "organizations", "items");
  const licByOrg = useMemo(() => Object.fromEntries((billing.data?.licenses || []).map((l) => [l.org_id, l])), [billing.data]);
  const filtered = list.filter((o) => !q || String(o.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.nav.owner")} title={t("b2b.owner.orgs")}
        actions={<Link to="/admin/organizations" data-testid="owner-manage-orgs" className="h-10 px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center">{t("platform.createOrg") || "Create organisation"}</Link>} />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("b2b.owner.searchOrgs")} className="h-11 rounded-xl bg-white max-w-md" data-testid="owner-org-search" />
      <div className="rounded-3xl bg-white shadow-card overflow-hidden" data-testid="owner-org-table">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.15em] text-ink-500 text-left">
            <tr className="border-b border-border">
              <th className="px-5 py-3">{t("nav.organisation")}</th><th className="px-5 py-3">{t("b2b.owner.status")}</th>
              <th className="px-5 py-3">{t("b2b.billing.seats")}</th><th className="px-5 py-3">{t("b2b.nav.billing")}</th>
              <th className="px-5 py-3">{t("b2b.owner.created")}</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((o) => {
              const id = o.id || o._id; const lic = licByOrg[id];
              return (
                <tr key={id} className="hover:bg-cream-50" data-testid={`owner-org-${id}`}>
                  <td className="px-5 py-3 font-medium text-ink-900">{o.name}<div className="text-xs text-ink-500">{o.organization_type || o.type || "business"}</div></td>
                  <td className="px-5 py-3"><StatusPill status={o.status || "active"} /></td>
                  <td className="px-5 py-3 text-ink-700">{lic ? `${lic.seats_purchased}` : (o.license_limit ?? o.seat_limit ?? "—")}</td>
                  <td className="px-5 py-3">{lic ? <StatusPill status={lic.status} label={t(`b2b.billing.states.${lic.status}`)} /> : <span className="text-xs text-ink-500">{t("b2b.owner.noStripe")}</span>}</td>
                  <td className="px-5 py-3 text-ink-500">{fmtDate(o.created_at)}</td>
                  <td className="px-5 py-3 text-right"><Link to={`/owner/organizations/${id}`} className="text-sage-600 font-semibold" data-testid={`owner-org-open-${id}`}>{t("b2b.owner.openOrg")}</Link></td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={6} className="px-5 py-6 text-ink-500">{orgs.isLoading ? t("common.loading") : t("common.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
